<script>
export default {
  name: "HomeCareerPlan",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: ""
    },
    color: {
      type: String,
      default: "bronze" // 'bronze' | 'silver' | 'gold'
    },
    target: {
      type: Number,
      default: 0
    },
    current: {
      type: Number,
      default: 0
    },
    active: {
      type: Boolean,
      default: false
    },
    benefitText: {
      type: String,
      default: ""
    },
    totalReceived: {
      type: Number,
      default: 0
    },
    forecast: {
      // { value: Number, missingText }
      type: Object,
      default: null
    }
  },
  computed: {
    progressPercent() {
      if (!this.target) return 0;
      return Math.min(100, Math.round((this.current / this.target) * 100));
    },
    hasReceived() {
      return this.totalReceived > 0;
    }
  }
};
</script>

<template>
  <div class="card home-career-plan h-100">
    <div class="card-body d-flex flex-column">
      <template v-if="loading">
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div class="d-flex align-items-center">
            <div>
              <div class="font-size-16 mb-1"><span class="skeleton-block" style="width: 90px; height: 14px;"></span></div>
              <div class="font-size-12"><span class="skeleton-block" style="width: 70px; height: 10px;"></span></div>
            </div>
          </div>
          <span class="skeleton-block skeleton-pill" style="width: 60px; height: 20px;"></span>
        </div>

        <div class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <span class="skeleton-block" style="width: 140px; height: 10px;"></span>
            <span class="skeleton-block" style="width: 34px; height: 10px;"></span>
          </div>
          <div class="home-career-plan-progress-track">
            <div class="skeleton-block" style="width: 100%; height: 100%; border-radius: 999px;"></div>
          </div>
        </div>

        <div class="mb-3">
          <div class="skeleton-block mb-1" style="width: 100%; height: 10px;"></div>
          <div class="skeleton-block" style="width: 70%; height: 10px;"></div>
        </div>

        <div class="mt-auto">
          <div class="home-career-plan-box mb-2">
            <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 100px; height: 10px;"></span></div>
            <div class="home-career-plan-box-value"><span class="skeleton-block" style="width: 90px; height: 17px;"></span></div>
          </div>
          <div class="home-career-plan-box">
            <div class="font-size-12 text-uppercase mb-1"><span class="skeleton-block" style="width: 120px; height: 10px;"></span></div>
            <div class="home-career-plan-box-value"><span class="skeleton-block" style="width: 90px; height: 17px;"></span></div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div class="d-flex align-items-center">
            <div>
              <div class="font-size-18 font-weight-bold">{{ name }}</div>
              <div class="font-size-12 text-muted notranslate">{{ target | points }} assinaturas</div>
            </div>
          </div>
          <div class="badge-pill" :class="active ? 'badge-pill-success' : 'badge-pill-danger'">
            {{ active ? 'Ativo' : 'Inativo' }}
          </div>
        </div>

        <div class="mb-3">
          <div class="d-flex justify-content-between text-muted mb-1">
            <span class="notranslate">{{ current | points }} / {{ target | points }} assinaturas</span>
            <span class="font-weight-bold notranslate" :class="active ? 'text-success' : 'text-muted'">{{ progressPercent }}%</span>
          </div>
          <div class="home-career-plan-progress-track">
            <div class="home-career-plan-progress-bar" :class="active ? 'home-career-plan-progress-bar-success' : ''" :style="{ width: progressPercent + '%' }"></div>
          </div>
        </div>

        <p class="mb-3" v-html="benefitText"></p>

        <div class="mt-auto">
          <div v-if="forecast" class="home-career-plan-box mb-2" :class="forecast.value > 0 ? 'home-career-plan-box-warning' : ''">
            <div class="font-size-12 font-weight-semibold text-uppercase">Previsão ao desbloquear</div>
            <div class="home-career-plan-box-value notranslate">{{ forecast.value | currency }}</div>
            <div v-if="forecast.missingText" class="font-size-11">{{ forecast.missingText }}</div>
          </div>
          <div class="home-career-plan-box" :class="hasReceived ? 'home-career-plan-box-success' : ''">
            <div class="font-size-12 font-weight-semibold text-uppercase">Total já recebido</div>
            <div class="home-career-plan-box-value notranslate">{{ totalReceived | currency }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.home-career-plan-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.home-career-plan-icon-bronze {
  background-color: #fef3e2;
  color: #b45309;
}

.home-career-plan-icon-silver {
  background-color: #f1f5f9;
  color: #64748b;
}

.home-career-plan-icon-gold {
  background-color: #fefce8;
  color: #ca8a04;
}

.home-career-plan-progress-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.home-career-plan-progress-bar {
  height: 100%;
  background: #d1d5db;
}

.home-career-plan-progress-bar-success {
  background: #059669;
}

.home-career-plan-box {
  padding: 12px 14px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.home-career-plan-box-value {
  font-size: 17px;
  font-weight: 600;
}

.home-career-plan-box-success {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

.home-career-plan-box-warning {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.badge-pill-success {
  background-color: #d1fae5;
  color: #065f46;
}

.badge-pill-warning {
  background-color: #fef3c7;
  color: #92400e;
}

.badge-pill-danger {
  background-color: #fee2e2;
  color: #991b1b;
}

.skeleton-block {
  display: inline-block;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e4e7 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.skeleton-pill {
  border-radius: 999px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
</style>
